import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PackageCheck, TriangleAlert } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchReplenishment, formatQuantity, may, nameIn, pluralise, sendReplenishment,
  translateError, type ReplenishmentPantry, type ReplenishmentRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-108 — WHAT TO SEND UP THIS MORNING.  Sprint 67.

   ── W-100 SHOWS BALANCES; THIS IS A PICKING LIST ───────────────────────────────
   The stock board answers *what is where*, which is the right screen for a question about
   the past. A store keeper at seven in the morning is asking something else — *what do I
   put on the trolley and take upstairs?* — and a table of balances makes him do the
   subtraction for every item on every floor, in his head, before he can start.

   So the subtraction is done once, per pantry, and one fact a balances screen cannot show
   at all is beside it: **whether the store can actually cover it**. That is a fact about
   two places at the same time.

   ── IT COULD NOT HAVE BEEN BUILT BEFORE W-104 ──────────────────────────────────
   Par was one number per item until this sprint — the same forty towels for the main
   store, four pantries, the laundry and every pair's trolley. A picking list on that would
   have proposed filling the laundry with clean towels every morning for ever.

   ── AND IT PROPOSES RATHER THAN ACTS ───────────────────────────────────────────
   The quantities are editable, and what is sent is what the store keeper typed. The same
   judgement the client made about allocation on 18 September — *the system proposes, a
   person approves* — and for the same reason: he knows about the delivery due this
   afternoon and the floor closed for painting, and the par table does not.
   ========================================================================= */

export function Replenishment({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  /* **WHAT HE HAS TYPED, KEYED BY PLACE AND ITEM.** Absent means "the suggestion stands";
     it is not seeded from the board, because a map pre-filled with every suggestion cannot
     tell an untouched row from one he deliberately set back to its suggestion. */
  const [typed, setTyped] = useState<Record<string, number>>({})
  const [sent, setSent] = useState<number>()

  const board = useQuery({
    queryKey: ['replenishment', propertyId],
    queryFn: ({ signal }) => fetchReplenishment(propertyId, { signal }),
    retry: false,
  })

  const send = useMutation({
    mutationFn: (lines: { stockLocationId: string; stockItemId: string; quantity: number }[]) =>
      sendReplenishment(propertyId, lines),
    onSuccess: (result) => {
      setSent(result.moved)
      setTyped({})
      /* THE BOARD AND THE STOCK BOARD BOTH: a transfer changes two balances, and a stock
         screen left open beside this one would be wrong within a second. */
      void queryClient.invalidateQueries({ queryKey: ['replenishment', propertyId] })
      void queryClient.invalidateQueries({ queryKey: ['stock-levels', propertyId] })
      void queryClient.invalidateQueries({ queryKey: ['stock-on-hand', propertyId] })
    },
  })

  const quantityFor = (pantryId: string, row: ReplenishmentRow) =>
    typed[`${pantryId}:${row.stockItemId}`] ?? row.suggested

  const lines = useMemo(() => {
    const out: { stockLocationId: string; stockItemId: string; quantity: number }[] = []
    for (const pantry of board.data?.pantries ?? [])
      for (const row of pantry.rows) {
        const quantity = typed[`${pantry.stockLocationId}:${row.stockItemId}`] ?? row.suggested
        if (quantity > 0)
          out.push({
            stockLocationId: pantry.stockLocationId,
            stockItemId: row.stockItemId,
            quantity,
          })
      }
    return out
  }, [board.data, typed])

  if (board.isLoading && !board.error) return <LoadingState rows={5} />

  if (board.error && !board.data) {
    if (board.error instanceof ApiError && board.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(board.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={board.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = board.data!
  const mayMove = may('stock.move')

  return (
    <div className="space-y-4 p-4">
      {/* **THE PURCHASE ORDER, ABOVE THE PICKING LIST.** A pantry below par is Tuesday and
          a store that cannot fill its pantries is a different problem for a different
          person — so it is not a row in the table, it is a card above it. */}
      {view.shortOverall.length > 0 && (
        <Card>
          <CardHeader
            title={t.rpShortTitle}
            icon={<TriangleAlert className="size-4" />}
          />
          <CardBody className="space-y-1.5">
            <p className="text-sm text-muted-foreground">{t.rpShortBody}</p>
            {view.shortOverall.map((s) => (
              <p key={s.stockItemId} className="text-sm">
                <bdi className="font-medium">{nameIn(locale, s.itemName, s.itemNameAr)}</bdi>
                {' — '}
                <span className="tnum">
                  {/* **NO UNIT IN THIS SENTENCE.** It read "40 piece needed", because the
                      units in this product are stored singular — and pluralising a word
                      that arrives from reference data is a translation problem, not a
                      sentence. Both numbers are in the same unit as the item beside them,
                      so the unit adds nothing and the awkwardness was all it contributed. */}
                  {fill(t.rpShortLine, {
                    needed: formatQuantity(s.neededAcrossPantries, locale),
                    held: formatQuantity(s.inStore, locale),
                  })}
                </span>
              </p>
            ))}
          </CardBody>
        </Card>
      )}

      {sent !== undefined && (
        <p role="status" className="rounded-hk border border-border bg-muted/40 px-3 py-2 text-sm">
          {pluralise(t.rpSent, sent, locale, formatQuantity(sent, locale))}
        </p>
      )}

      {send.error !== null && send.error !== undefined && (
        <p role="alert" className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
          {String(translateError(send.error, locale).message)}
        </p>
      )}

      {view.pantries.length === 0 ? (
        <Card>
          <CardHeader title={t.rpTitle} icon={<PackageCheck className="size-4" />} />
          <CardBody>
            <EmptyState title={t.rpNothingShort} />
          </CardBody>
        </Card>
      ) : (
        <>
          {view.pantries.map((pantry) => (
            <PantryCard
              key={pantry.stockLocationId}
              pantry={pantry}
              quantityFor={(row) => quantityFor(pantry.stockLocationId, row)}
              onChange={(row, quantity) => setTyped((all) => ({
                ...all, [`${pantry.stockLocationId}:${row.stockItemId}`]: quantity,
              }))}
              disabled={!mayMove || send.isPending}
            />
          ))}

          <div className="flex items-center justify-end gap-3">
            <span className="text-sm text-muted-foreground">
              {pluralise(t.rpLines, lines.length, locale,
                formatQuantity(lines.length, locale))}
            </span>
            <Button
              type="button"
              disabled={!mayMove || lines.length === 0 || send.isPending}
              title={mayMove ? undefined : t.rpNeedsOtherRole}
              onClick={() => send.mutate(lines)}
            >
              <PackageCheck className="size-3.5" aria-hidden />
              {t.rpSend}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function PantryCard({
  pantry, quantityFor, onChange, disabled,
}: {
  pantry: ReplenishmentPantry
  quantityFor: (row: ReplenishmentRow) => number
  onChange: (row: ReplenishmentRow, quantity: number) => void
  disabled: boolean
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <Card>
      <CardHeader
        title={nameIn(locale, pantry.name, pantry.nameAr)}
        icon={<PackageCheck className="size-4" />}
        actions={pantry.floorCode
          ? <span className="text-[12px] text-muted-fg">
              {fill(t.rpOnFloor, { floor: pantry.floorCode })}
            </span>
          : undefined}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-border text-[11.5px] text-muted-foreground">
              <th className="px-3 py-2 text-start font-medium">{t.stockItem}</th>
              <th className="px-3 py-2 text-end font-medium">{t.rpHas}</th>
              <th className="px-3 py-2 text-end font-medium">{t.stockPar}</th>
              <th className="px-3 py-2 text-end font-medium">{t.rpInStore}</th>
              <th className="px-3 py-2 text-end font-medium">{t.rpSendCol}</th>
            </tr>
          </thead>
          <tbody>
            {pantry.rows.map((row) => {
              const quantity = quantityFor(row)
              /* **SAID WHERE IT HAPPENS.** A suggestion smaller than the shortfall is not a
                 rounding — it is the store running out — and a store keeper who is not told
                 will fill the gap from somewhere and wonder later why the floor was short. */
              const capped = row.suggested < row.shortfall

              return (
                <tr key={row.stockItemId} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-2">
                    <bdi className="text-foreground">
                      {nameIn(locale, row.itemName, row.itemNameAr)}
                    </bdi>
                    {capped && (
                      <span className="ms-2 text-[11.5px]"
                            style={{ color: 'var(--hk-hk-dirty-text)' }}>
                        {t.rpStoreShort}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end tnum font-semibold
                                 text-[#b45309] dark:text-[#fbbf24]">
                    {formatQuantity(row.inPantry, locale)}
                  </td>
                  <td className="px-3 py-2 text-end tnum text-muted-foreground">
                    {formatQuantity(row.parLevel, locale)}
                  </td>
                  <td className="px-3 py-2 text-end tnum text-muted-foreground">
                    {formatQuantity(row.inStore, locale)}
                  </td>
                  <td className="px-3 py-2 text-end">
                    {/* **WESTERN DIGITS IN THE BOX, IN BOTH LANGUAGES.** A number somebody
                        types and a number the product prints are different things: this is
                        an input, and an Arabic-Indic numeral typed into `type="number"` is
                        not a number to a browser. */}
                    <input
                      type="number"
                      min={0}
                      step={1}
                      dir="ltr"
                      disabled={disabled}
                      aria-label={`${nameIn(locale, row.itemName, row.itemNameAr)} — ${t.rpSendCol}`}
                      value={quantity}
                      onChange={(e) => onChange(row, Math.max(0, Number(e.target.value) || 0))}
                      className="w-20 rounded-hk border border-border bg-background px-2 py-1
                                 text-end text-[13px] tnum disabled:opacity-50"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
